import {
  CompliancePageDealDocumentsEmailTrackingFilterableInvestmentsQuery,
  CompliancePageDealDocumentsEmailTrackingListQuery,
  FatInvestorsPageAllAdvisoryFirmListQuery
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { Loader } from '@/components/loaders/loader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { SORT_ORDER, TableColumns } from '@/components/table/types';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { dealDocumentsTrackingMobileTableSort, dealDocumentsTrackingTableColumns } from '../constants';
import {
  COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_FILTERABLE_INVESTMENTS_QUERY,
  COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_QUERY
} from '../queries';
import { DealDocumentsTrackingTable } from './table/table';
import DealDocumentsTrackingMobileTable from './table/tableMobile';

export const SendDealDocumentsTracking = () => {
  const goBack = useGoBack();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { searchParams, onChangeSort, onChangeSearch, onChangePrimaryFilter, onChangePaginationData, makeScroll, saveScrollPostion } =
    useSearchParamsManager();

  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;
  const backToTitle = searchParams.get('backToTitle') || '';
  const q = searchParams.get('q') || '';
  const strategyQuickFilter = searchParams.get('quickFilter') || '';
  const key = searchParams.get('key');
  const asc = searchParams.get('asc') === 'true' || false;
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const advisoryFirmQuickFilter = searchParams.get('advisoryFirmQuickFilter') || '';
  const position = searchParams.get('position') || 0;

  const [strategyQuickFilterOptions, setStrategyQuickFilterOptions] = useState<ISelectValue[]>([]);
  const [advisoryFirmQuickFilterList, setAdvisoryFirmQuickFilterList] = useState<ISelectValue[]>([]);
  const [tableColumns, setTableColumns] = useState<TableColumns[]>(dealDocumentsTrackingTableColumns);
  const [dealDocumentsEmailTrackingTableRows, setDealDocumentsEmailTrackingTableRows] = useState<
    CompliancePageDealDocumentsEmailTrackingListQuery['dealDocumentsEmailTrackingList']['dealDocumentTrackingData']
  >([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mobileSortItems, setMobileSortItems] = useState(dealDocumentsTrackingMobileTableSort);
  const [mobileSort, setMobileSort] = useState(dealDocumentsTrackingMobileTableSort[14].title);
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  useQuery<{ allAdvisoryFirmList: FatInvestorsPageAllAdvisoryFirmListQuery['allAdvisoryFirmList'] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: !isPlatformManagerUser,
    onCompleted: ({ allAdvisoryFirmList }) => {
      setAdvisoryFirmQuickFilterList([
        { label: 'All', value: '' },
        ...new Set(allAdvisoryFirmList.map((el) => createSimpleSelectOption(el.id, el.name)))
      ]);
    }
  });

  useQuery<CompliancePageDealDocumentsEmailTrackingFilterableInvestmentsQuery>(
    COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_FILTERABLE_INVESTMENTS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        data: {
          perPage: 1000,
          page: 1
        }
      },
      onCompleted: (data) => {
        const { filterableInvestments } = data.dealDocumentsEmailTrackingList;

        setStrategyQuickFilterOptions([
          { label: 'All', value: '' },
          ...(filterableInvestments?.map((el) => createSimpleSelectOption(el.id, el.name)) || [])
        ]);
      }
    }
  );

  const {
    data: dealDocumentsEmailTrackingListData,
    loading: dealDocumentsEmailTrackingListLoading,
    refetch: dealDocumentsEmailTrackingListRefetch
  } = useQuery<CompliancePageDealDocumentsEmailTrackingListQuery>(COMPLIANCE_PAGE_DEAL_DOCUMENTS_EMAIL_TRACKING_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        tenantId: advisoryFirmQuickFilter,
        investmentId: strategyQuickFilter,
        order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        sort: key || 'lastUpdatedAt',
        search: q,
        perPage: Number(limit),
        page: Math.floor(Number(offset) / Number(limit)) + 1
      }
    },
    onCompleted: (data) => {
      const { dealDocumentTrackingData } = data.dealDocumentsEmailTrackingList;

      setDealDocumentsEmailTrackingTableRows(dealDocumentTrackingData || []);
      makeScroll(position);
    }
  });

  useEffect(() => {
    if (user && user?.tenant?.type === TenantType.ADVISORY_FIRM) {
      const updatedTableColumns = dealDocumentsTrackingTableColumns.map((column) => {
        if (column.key !== 'advisoryFirm') {
          return column;
        }
        return { ...column, width: 0, title: '' };
      });
      setTableColumns(updatedTableColumns);

      const updatedMobileSortItems = dealDocumentsTrackingMobileTableSort.filter((item) => item.key !== 'advisoryFirm');
      setMobileSortItems(updatedMobileSortItems);
    }
  }, [user]);

  useEffect(() => {
    if (!isMobile) return;

    dealDocumentsEmailTrackingListRefetch({
      data: {
        order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        sort: sortItem?.key
      }
    });
  }, [mobileSort]);

  const openPage = (path: string) => {
    saveScrollPostion();
    navigate(path);
  };

  const handleDownloadDealDocumentEmailTracking = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/allocation/deal-document-email-tracking-export`, {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      fileDownload(response.data, 'deal-document-email-tracking-export.csv');
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Generating" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/compliance' })} backToTitle={backToTitle} />} />
        <PageTitle title="Send Deal Documents Tracking" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search entity, investment, client'
            }}
            {...(isPlatformManagerUser && {
              advisoryFirmQuickFilter: {
                title: 'Advisory Firm',
                value: advisoryFirmQuickFilter,
                defaultValue: 'All',
                options: advisoryFirmQuickFilterList,
                onChange: (value) => onChangePrimaryFilter(value, 'advisoryFirmQuickFilter')
              }
            })}
            quickFilter={{
              title: 'Strategy',
              value: strategyQuickFilter,
              defaultValue: 'All',
              options: strategyQuickFilterOptions,
              onChange: (value) => onChangePrimaryFilter(value, 'quickFilter')
            }}
            downloadButton={{
              onClick: handleDownloadDealDocumentEmailTracking
            }}
            resultsValue={dealDocumentsEmailTrackingListData?.dealDocumentsEmailTrackingList?.total || 0}
            refetch={dealDocumentsEmailTrackingListRefetch}
            mobileSort={{ value: mobileSort, onChange: setMobileSort, selects: mobileSortList }}
            useNewFilters
          />
          {isMobile ? (
            <>
              {dealDocumentsEmailTrackingListLoading ? (
                <Loader />
              ) : (
                <DealDocumentsTrackingMobileTable tableColumns={tableColumns} tableRows={dealDocumentsEmailTrackingTableRows} openPage={openPage} />
              )}
            </>
          ) : (
            <DealDocumentsTrackingTable
              tableColumns={tableColumns}
              tableRows={dealDocumentsEmailTrackingTableRows}
              sort={{ key, asc }}
              loading={dealDocumentsEmailTrackingListLoading}
              setSort={onChangeSort}
              openPage={openPage}
            />
          )}
          <PaginationWrap>
            <TablePagination
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: dealDocumentsEmailTrackingListData?.dealDocumentsEmailTrackingList.total ?? 0
              }}
              savePagination={onChangePaginationData}
              refetch={() => {}}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
